/** Sending a recording file to the server in pieces (the Upload page).
 *
 * Pieces go one at a time, in order, from `next`. A failed piece is retried
 * with a growing wait, for as long as the page is open: on a train, a laptop
 * lid closed, a dropped Wi-Fi — the upload carries on when the line is back.
 * The server refuses a piece from the future ("Expected piece 7 first"), and
 * then the upload goes back to that one. */

import { ApiError, NotSignedIn, uploadFilePiece } from "./api";

const RETRY_MS = [2_000, 5_000, 10_000, 20_000, 30_000];
const EXPECTED = /Expected piece (\d+)/;

export interface UploadProgress {
  /** Bytes that have arrived, counting the piece on its way. */
  sent: number;
  size: number;
  /** While a piece is being retried: why. Null once it's through. */
  problem: string | null;
}

export async function sendFile(file: File, sessionId: string, pieceBytes: number, next: number, csrfToken: string,
                               onProgress: (progress: UploadProgress) => void, signal: AbortSignal): Promise<void> {
  const count = Math.ceil(file.size / pieceBytes);
  let seq = next;
  let attempt = 0;
  let problem: string | null = null;
  while (seq < count) {
    if (signal.aborted) throw new DOMException("Stopped", "AbortError");
    const start = seq * pieceBytes;
    const piece = file.slice(start, Math.min(start + pieceBytes, file.size));
    try {
      await uploadFilePiece(sessionId, seq, piece, csrfToken,
                            (loaded) => onProgress({ sent: start + loaded, size: file.size, problem }), signal);
      seq += 1;
      attempt = 0;
      problem = null;
      onProgress({ sent: Math.min(seq * pieceBytes, file.size), size: file.size, problem });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      const expected = error instanceof ApiError && error.status === 409 ? EXPECTED.exec(error.message) : null;
      if (expected) {
        seq = Number(expected[1]);
        continue;
      }
      // Too big, refused, gone: retrying won't help.
      if (error instanceof ApiError && [400, 404, 413].includes(error.status)) throw error;
      problem = error instanceof NotSignedIn
        ? "You've been signed out. Sign in again in another tab and the upload carries on."
        : `${(error as Error).message || "The connection dropped"} — trying again…`;
      onProgress({ sent: start, size: file.size, problem });
      await wait(RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)], signal);
      attempt += 1;
    }
  }
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(timer);
      reject(new DOMException("Stopped", "AbortError"));
    }, { once: true });
  });
}

/** The date a file says it was made, for the form: the browser only knows when
 * it was last changed, which for a recording is usually when it was saved. */
export function fileDate(file: File): Date {
  return new Date(file.lastModified || Date.now());
}

export const MAX_UPLOAD_BYTES = 2 * 1024 ** 3;
export const AUDIO_EXTENSIONS = [".mp3", ".m4a", ".wav", ".ogg", ".oga", ".opus", ".aac", ".flac", ".wma", ".amr"];
export const VIDEO_EXTENSIONS = [".mp4", ".mov", ".m4v", ".mkv", ".webm", ".avi", ".wmv", ".3gp", ".mpeg", ".mpg", ".ts"];

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot).toLowerCase() : "";
}

/** 340 MB, 1.2 GB */
export function bytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${Math.round(n / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}
